import { Button, Icon, LoadingSpinner } from '@idexx-laboratories/spot-react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentHeader from '../../components/ContentHeader/ContentHeader';
import { useStore } from '../../hooks/use-store';
import { PopoverRenderer, UpgradeVersionContent } from './renderers';
import './UpgradeVersionManagement.scss';

const UpgradeVersionManagement = observer(() => {
  const store = useStore().upgradeVersionManagementStore;
  const { loaders, selectedVersionIndex, editMode, spinnerVersionIndex } = store;
  const navigate = useNavigate();

  useEffect(() => {
    void store.init();
    return () => store.close();
  }, [store]);

  const focusRef = useCallback(
    (el: HTMLDivElement | null, index) => {
      if (index === selectedVersionIndex) el?.scrollIntoView({ block: 'center' });
    },
    [selectedVersionIndex],
  );

  const contentHeaderClick = useCallback(
    (index: number, e: any) => {
      if (!e.target.closest('.actions-popover')) store.changeSelectedVersion(index);
    },
    [store],
  );

  if (loaders.page) return <LoadingSpinner size='large' />;

  return (
    <div className='upgrade-version-management_wrapper'>
      <div className='spot-typography__heading--level-2'>Upgrade Version Management</div>
      <div className='spot-typography__text--tertiary'>
        Manage which versions of Cornerstone can be upgraded to using the upgrade service and view
        details for each version.
      </div>
      <div className='page-top-actions'>
        <Button
          className='add-version-btn'
          aria-label='Add Version'
          variant='link'
          size='small'
          iconLeft='plus'
          disabled={loaders.save || editMode}
          onClick={() => store.addVersion()}
        >
          Add version
        </Button>
      </div>
      {store.versions.map((version, index) => (
        <div className='version-header' key={index} ref={el => focusRef(el, index)}>
          <ContentHeader
            id={`expand-toggle-${index}`}
            className='spot-typography__heading--level-5 expand-toggle horizontal-flex-box'
            onClick={(e: any) => contentHeaderClick(index, e)}
            data-testid={`expandable-${version.version}`}
          >
            <div>
              {selectedVersionIndex === index ? (
                <Icon size='l' className='caret-icon' iconName='caret-up' title='collapse' />
              ) : (
                <Icon size='l' className='caret-icon' iconName='caret-down' title='expand' />
              )}
              &nbsp;{version.version}
              {version.default ? ' (Default)' : ''}&nbsp;
              {loaders.save && spinnerVersionIndex === index && <LoadingSpinner />}
            </div>
            <div>
              {editMode ? (
                selectedVersionIndex === index && (
                  <>
                    <Button
                      variant='link'
                      iconOnly='save'
                      onClick={() => void store.saveVersion()}
                      disabled={loaders.save}
                      title='Save changes'
                      aria-label='Save changes'
                      data-testid={`save-${index}-btn`}
                    />
                    <Button
                      variant='link'
                      iconOnly='cancel'
                      disabled={loaders.save}
                      onClick={() => store.cancelEditing()}
                      title='Cancel changes'
                      aria-label='Cancel changes'
                      data-testid={`cancel-${index}-btn`}
                    />
                  </>
                )
              ) : (
                <PopoverRenderer index={index} />
              )}
            </div>
          </ContentHeader>
          <div
            className={classNames('version-content', { collapsed: selectedVersionIndex !== index })}
          >
            <UpgradeVersionContent index={index} />
          </div>
        </div>
      ))}
      <div className='page-bottom-actions'>
        <Button className='back' variant='link' size='small' onClick={() => navigate(-1)}>
          Back
        </Button>
        <div className='main-buttons'>
          <Button variant='primary' size='small' onClick={() => navigate('/')}>
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
});

export default UpgradeVersionManagement;
